import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Download, PieChart, MapPin, StopCircle } from "lucide-react";
import { motion } from "framer-motion";
import { generateTripReport } from "@/utils/pdfGenerator";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { TripInsights } from "./TripInsights";
import { Expense } from "@/types/expense";
import { useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface TripManagerProps {
  expenses: Expense[];
  tripName: string;
  onEndTrip?: () => void;
}

export const TripManager = ({ expenses, tripName, onEndTrip }: TripManagerProps) => {
  const [showEndTripDialog, setShowEndTripDialog] = useState(false);

  const handleExportReport = () => {
    if (expenses.length === 0) {
      toast.error("No expenses to export");
      return;
    }
    generateTripReport(tripName || "Current Trip", expenses);
    toast.success("Report Generated", {
      description: "PDF report has been downloaded."
    });
  };

  const handleEndTrip = () => {
    if (expenses.length === 0) {
      toast.error("Cannot end trip without any expenses", {
        description: "Please add at least one expense before ending the trip."
      });
      setShowEndTripDialog(false);
      return;
    }

    if (onEndTrip) {
      onEndTrip();
      setShowEndTripDialog(false);
      toast.success("Trip Ended", {
        description: "Your trip has been moved to history."
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card className="p-4 md:p-6 bg-gradient-to-br from-primary/5 to-primary/10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="space-y-2 w-full md:w-auto">
            <h2 className="text-xl md:text-2xl font-semibold flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary animate-pulse" />
              {tripName || "Current Trip"}
            </h2>
            <p className="text-sm text-muted-foreground">
              Track your expenses and manage your current trip
            </p>
          </div>
          <ScrollArea className="w-full md:w-auto">
            <div className="flex flex-wrap md:flex-nowrap gap-2 min-w-[300px] md:min-w-0">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="flex items-center gap-2 whitespace-nowrap">
                    <PieChart className="h-4 w-4" />
                    Trip Insights
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden">
                  <DialogHeader>
                    <DialogTitle>Trip Insights</DialogTitle>
                    <DialogDescription>
                      Analyze your trip expenses and download reports
                    </DialogDescription>
                  </DialogHeader>
                  <div className="overflow-y-auto pr-4 max-h-[calc(80vh-8rem)]">
                    <TripInsights expenses={expenses} />
                  </div>
                </DialogContent>
              </Dialog>
              
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2 whitespace-nowrap"
                onClick={handleExportReport}
              >
                <Download className="h-4 w-4" />
                Export Report
              </Button>

              <Dialog open={showEndTripDialog} onOpenChange={setShowEndTripDialog}>
                <DialogTrigger asChild>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="flex items-center gap-2 whitespace-nowrap"
                  >
                    <StopCircle className="h-4 w-4" />
                    End Trip
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>End Trip</DialogTitle>
                    <DialogDescription>
                      Are you sure you want to end this trip? This action will move all expenses to history and reset current trip data.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => setShowEndTripDialog(false)}>Cancel</Button>
                    <Button variant="destructive" onClick={handleEndTrip}>End Trip</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </ScrollArea>
        </div>
      </Card>
    </motion.div>
  );
};